import { createSignal, Show, type Component } from "solid-js";
import type { RichTextEditorInstance } from "../types";
import { BubbleMenu } from "tiptap-solid";
import { XMarkIcon } from "@storytell/bsky-icons";
import type { PmInline, PmNode } from "@storytell/types/richText";
import type { GlobalEventData } from "@storytell/types";

export const EditorBubbleMenu: Component<{
  editor: RichTextEditorInstance | null;
  onQuoteInPromptBar?: (data: GlobalEventData<"quote-in-prompt-bar">) => void;
}> = (props) => {
  const [state, setState] = createSignal<"normal" | "formatting">("normal");
  return (
    <Show when={props.editor}>
      {(editor) => (
        <BubbleMenu editor={editor()} updateDelay={0} tippyOptions={{ delay: 0, duration: 120 }}>
          <div class="bg-tile border border-primary-border shadow-xl flex items-center gap-2 text-sm rounded-full px-3 overflow-hidden">
            <Show
              when={state() === "normal"}
              fallback={
                <>
                  <button
                    type="button"
                    onClick={() => setState("normal")}
                    class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center"
                  >
                    <XMarkIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor().chain().focus().toggleBold().run()}
                    data-active={editor().isActive("bold") || undefined}
                    class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center data-active:font-bold data-active:bg-background"
                  >
                    B
                  </button>
                  <button
                    type="button"
                    data-active={editor().isActive("italic") || undefined}
                    onClick={() => editor().chain().focus().toggleItalic().run()}
                    class="cursor-pointer bg-tile hover:bg-tile-hover h-8 w-8 grid place-content-center data-active:font-bold data-active:bg-background"
                  >
                    I
                  </button>
                </>
              }
            >
              <button
                type="button"
                onClick={() => setState("formatting")}
                class="cursor-pointer bg-tile hover:bg-tile-hover underline h-8 w-8 grid place-content-center"
              >
                Aa
              </button>
            </Show>

            <Show when={state() === "normal"}>
              <div class="h-4 w-[1px] bg-inset" />
              <button
                type="button"
                onClick={() => {
                  const nodes: (PmNode | PmInline)[] = [];
                  const { state } = editor();
                  const { from, to } = state.selection;
                  const text = state.doc.textBetween(from, to, "\n\n");

                  if (nodes) {
                    props.onQuoteInPromptBar?.({
                      quote: nodes,
                      text,
                    });
                  }
                }}
                class="cursor-pointer bg-tile hover:bg-tile-hover h-8 flex items-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="size-4"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <path d="M10 11h-4a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1h3a1 1 0 0 1 1 1v6c0 2.667 -1.333 4.333 -4 5" />
                  <path d="M19 11h-4a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1h3a1 1 0 0 1 1 1v6c0 2.667 -1.333 4.333 -4 5" />
                </svg>

                <span>Edit in chat</span>
              </button>
            </Show>
          </div>
        </BubbleMenu>
      )}
    </Show>
  );
};
